// Thin fetch wrappers for the app's JSON API.

// Re-export the canonical platform union from the shared scoring module so
// the client never duplicates the contract — when we add a new platform
// (or rename one), there's a single place to change.
export type { CanonicalPlatform } from "@shared/utm/types";
import type { CanonicalPlatform, UtmScoreResult } from "@shared/utm/types";
import type { GrantMode } from "@shared/auth/grant-mode";
export type { GrantMode };
// Structured-JSON insights contract — the client renders the skimmable
// UI (headline + preview + expandable detail + typed action cards) from
// the same schema the server validates against.
export type {
  InsightsAction,
  InsightsConfidence,
  InsightsLabel,
  InsightsSummary,
  InsightsTelemetry,
  InsightsTone,
  InsightsV2,
} from "@shared/insights/schema";
import type { InsightsTelemetry, InsightsV2 } from "@shared/insights/schema";

export interface DigestSubscriptionSummary {
  propertyId: string;
  weekday: number;
}

export interface BootstrapResponse {
  authenticated: boolean;
  env: string;
  user?: { id: string; email: string; name: string | null; picture: string | null };
  defaultProperty?: { propertyId: string; propertyName: string; accountName: string | null } | null;
  grantMode?: GrantMode;
  /** Epoch ms at which the currently cached GA4 access token expires.
   *  Null when no token is cached (eg. first load after an offline
   *  grant was revoked). Surfaced so Settings can show the user how
   *  long their live connection will keep working. */
  accessTokenExpiresAt?: number | null;
  /** Turnstile site key provisioned for this environment's hostname(s).
   *  Null when unset — the Turnstile widget falls back to its
   *  hard-coded prod key for hosts it was already tuned for. Allowing
   *  staging + prod to point at separate Cloudflare Turnstile sites
   *  avoids the "staging isn't validating" failure mode where a single
   *  site key is only whitelisted for prod on the Cloudflare dashboard. */
  turnstileSiteKey?: string | null;
  digestSubscriptions?: DigestSubscriptionSummary[];
}

export interface PropertySummary {
  propertyId: string;
  propertyName: string;
  propertyType?: string;
  accountId?: string;
  accountName?: string;
}

export async function fetchBootstrap(): Promise<BootstrapResponse> {
  const res = await fetch("/api/bootstrap", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`bootstrap failed: ${res.status}`);
  return (await res.json()) as BootstrapResponse;
}

export async function fetchProperties(): Promise<PropertySummary[]> {
  const res = await fetch("/api/properties", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`properties failed: ${res.status}`);
  const body = (await res.json()) as { properties: PropertySummary[] };
  return body.properties;
}

export async function setDefaultProperty(p: PropertySummary): Promise<void> {
  const res = await fetch("/api/properties/default", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (!res.ok) throw new Error(`set default failed: ${res.status}`);
}

export async function disconnect(): Promise<void> {
  const res = await fetch("/auth/disconnect", { method: "POST", credentials: "same-origin" });
  if (!res.ok) throw new Error(`disconnect failed: ${res.status}`);
}

export async function logout(): Promise<void> {
  const res = await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
  if (!res.ok) throw new Error(`logout failed: ${res.status}`);
}

// Thrown when /api/digest/subscribe returns 412 — the user is still on an
// ephemeral grant and needs to run the offline-upgrade OAuth flow before the
// subscription can be saved. The Settings page catches this and redirects.
export class OfflineGrantRequiredError extends Error {
  constructor() {
    super("offline_grant_required");
    this.name = "OfflineGrantRequiredError";
  }
}

export async function subscribeDigest(p: {
  propertyId: string;
  enabled?: boolean;
  weekday?: number;
}): Promise<void> {
  const res = await fetch("/api/digest/subscribe", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (res.status === 412) throw new OfflineGrantRequiredError();
  if (!res.ok) throw new Error(`subscribe failed: ${res.status}`);
}

export async function unsubscribeDigest(propertyId: string): Promise<void> {
  const res = await fetch(`/api/digest/subscribe?propertyId=${encodeURIComponent(propertyId)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`unsubscribe failed: ${res.status}`);
}

// M12.7 — one-off test digest send from Settings → Weekly digest →
// "Send sample report now". Returns { ok: true } on success; throws
// with a friendly message if the user has no subscription row yet,
// the SparkPost call fails, or the 3/hour rate limit kicks in.
export async function sendTestDigest(propertyId: string): Promise<void> {
  const res = await fetch("/api/digest/test-send", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ propertyId }),
  });
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  throw new Error(body.message ?? body.error ?? `test digest failed: ${res.status}`);
}

// Public undo pathway used by the UnsubscribePage when the user clicks
// "Accidentally unsubscribed?". Session-less — token is authz.
export async function resubscribeDigestViaToken(token: string): Promise<void> {
  const res = await fetch("/digest/resubscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const error = (body as { error?: string } | null)?.error ?? `status ${res.status}`;
    throw new Error(`resubscribe failed: ${error}`);
  }
}

// ---------------------------------------------------------------------------
// Report payload — mirrors the server-side ReportPayload from
// src/server/ga4/report-schema.ts. Slim TypeScript shape only — the backend
// Zod-validates the response, so we trust it client-side. If the contract
// drifts, M5.6 (states) is the natural place to promote a Zod schema into
// src/shared and revalidate here.
// ---------------------------------------------------------------------------

export type ReportRange = "last_7d" | "last_28d" | "last_90d";
// M12.2 — server also stamps "custom" on payloads built from bounds.
// Client consumers widen to ReportMetaRange when they read meta.range
// (eg. to look up a preset-only label).
export type ReportMetaRange = ReportRange | "custom";
// M12.12 — "custom" lets callers supply an arbitrary compare window via
// compareStart + compareEnd. Server falls back to "previous" behaviour
// if those bounds are missing, so the wire contract stays forgiving.
export type ReportCompare = "previous" | "year_over_year" | "none" | "custom";

export interface ReportDeltaTriplet {
  sessions: number | null;
  keyEvents: number | null;
  totalRevenue: number | null;
  /** Only populated on overview.deltas — per-platform rows don't track
   *  compare-period engagedSessions so the UI silently omits the chip. */
  engagementRate?: number | null;
  /** M12.4 — only populated when the user has configured conversion
   *  events for the property AND a compare period is active. */
  customConversions?: number | null;
  /** M13.2 — only populated on overview.deltas. Relative change in
   *  pages-per-session vs the compare period; null when compare is off
   *  or the previous denominator was zero. */
  pagesPerSession?: number | null;
  /** M13.4 — only populated on overview.deltas, and only when ecommerce
   *  is detected + windowActive. Relative change in transactions /
   *  AOV vs compare; null when compare is off or denominator is zero. */
  transactions?: number | null;
  averageOrderValue?: number | null;
}

export interface ReportOverviewMetrics {
  sessions: number;
  engagedSessions: number;
  engagementRate: number;
  keyEvents: number;
  sessionKeyEventRate: number;
  totalRevenue: number;
  totalUsers: number;
  /** M12.4 — only set when the user has configured events for the
   *  property. Undefined = render only the native keyEvents KPI. */
  customConversions?: number;
  /** M13.2 — pages-per-session and total pageviews for the social cohort.
   *  Server stamps both; client renders pagesPerSession in the KPI grid
   *  + uses pageviews as a sanity check on the DepthSection table totals. */
  pageviews: number;
  pagesPerSession: number;
  /** M13.4 — ecommerce KPI tile fields. Present only when ecommerce
   *  was detected AND the selected window had activity. KpiGrid hides
   *  the Transactions / AOV tiles when these are undefined. */
  transactions?: number;
  averageOrderValue?: number;
  deltas: ReportDeltaTriplet;
}

export interface ReportPlatformRow {
  platform: CanonicalPlatform;
  sessions: number;
  engagedSessions: number;
  keyEvents: number;
  sessionKeyEventRate: number;
  totalRevenue: number;
  topLandingPage: string | null;
  customConversions?: number;
  delta: ReportDeltaTriplet;
}

export interface ReportCountryRow {
  country: string;
  sessions: number;
  keyEvents: number;
  totalRevenue: number;
  customConversions?: number;
}

export interface ReportLandingPageRow {
  landingPage: string;
  pageUrl: string | null;
  platform: CanonicalPlatform;
  sessions: number;
  keyEvents: number;
  customConversions?: number;
}

export interface ReportRecommendation {
  category: string;
  severity: "severe" | "medium" | "mild";
  message: string;
  cta?: { label: string; href: string };
}

// ReportUtmHealth is the exact shape the server sends — UtmScoreResult
// from @shared/utm/types. Re-exported as an alias so call-sites still
// import it under the "Report*" naming.
export type ReportUtmHealth = UtmScoreResult;

export interface ReportPayload {
  meta: {
    propertyId: string;
    // M12.2 — widened so custom bounds don't blow up type checks on
    // meta.range consumers (eg. rangeLabel helpers). Preset callers
    // still see the narrow enum value they'd expect.
    range: ReportMetaRange;
    compare: ReportCompare;
    periodStart: string;
    periodEnd: string;
    comparePeriodStart: string | null;
    comparePeriodEnd: string | null;
    normalizationVersion: string;
    cachedAt: number;
    utmRowsTruncated: boolean;
    /** IANA timezone the server resolved the window against. Optional —
     *  legacy payloads built before M12.2 won't include this. */
    timezone?: string;
    /** M13.4 — ISO 4217 currency code resolved via GA4 Admin API
     *  properties.get + cached in property_connections.currency_code.
     *  Null when upstream couldn't resolve a code; the UI degrades to
     *  plain numbers in the ecommerce block. Optional so legacy share
     *  envelopes minted before M13.4 still parse. */
    currencyCode?: string | null;
  };
  overview: ReportOverviewMetrics;
  trend: Array<{
    date: string;
    // M12.5 — trend rows are now per-platform so clients can
    // filter/aggregate without a second API round-trip.
    platform: CanonicalPlatform;
    sessions: number;
    keyEvents: number;
    totalRevenue: number;
    customConversions?: number;
    // M13.2 — depth metrics. Per-platform per-day; the DepthSection
    // sums across platforms to drive the dual-axis chart + table.
    totalUsers: number;
    activeUsers: number;
    pageviews: number;
  }>;
  perPlatform: ReportPlatformRow[];
  perCountry: ReportCountryRow[];
  landingPages: ReportLandingPageRow[];
  utmHealth: ReportUtmHealth;
  recommendations: ReportRecommendation[];
  // M13.1 — macro channel mix across all 15 GA4 channel-group buckets.
  // Drives the SourceMixSection on the dashboard, the source-mix PDF
  // page, the XLSX "Source mix" sheet, and the digest "Wider context"
  // row. socialShare.sessions is a 0..1 ratio of (Organic Social + Paid
  // Social) over total sessions; socialShare.delta is the absolute ratio
  // change vs the compare period (null when compare is off).
  //
  // Optional in the client type (not in the server Zod schema) so the
  // SharedReportPage can read pre-M13.1 share-envelope blobs that omit
  // the block without breaking type checks. The /api/report path always
  // writes a populated block.
  sourceMix?: ReportSourceMix;
  // M13.3 — device breakdown (Desktop / Mobile / Tablet / Other) for
  // the social cohort. Drives MobileSection on the dashboard, the
  // Mobile PDF page, the XLSX "Mobile" sheet, and the digest
  // "Wider context" mobile-share row.
  //
  // Optional in the client type so legacy share envelopes minted before
  // M13.3 still parse without the block. The /api/report path always
  // writes it.
  mobile?: ReportMobile;
  // M13.4 — ecommerce breakdown. Two-stage gating:
  //   - probe-fail or pre-M13.4 envelope → null/undefined (block hidden)
  //   - probe-pass + window has activity → full block, windowActive=true
  //   - probe-pass + window empty       → block, windowActive=false
  // The dashboard renders <EcommerceSection> only when
  // ecommerce?.windowActive === true; the Transactions / AOV KPI tiles
  // and the conditional PlatformLeaderboard columns gate on the same
  // condition.
  ecommerce?: ReportEcommerce | null;
}

export type ReportDevice = "desktop" | "mobile" | "tablet" | "other";

export interface ReportMobilePerDeviceRow {
  device: ReportDevice;
  sessions: number;
  totalUsers: number;
  activeUsers: number;
  keyEvents: number;
  sessionKeyEventRate: number;
  totalRevenue: number;
}

export interface ReportMobileTrendRow {
  date: string;
  device: ReportDevice;
  sessions: number;
  totalUsers: number;
  activeUsers: number;
}

export interface ReportMobile {
  perDevice: ReportMobilePerDeviceRow[];
  trend: ReportMobileTrendRow[];
}

export interface ReportSourceMixChannelRow {
  channel: string;
  sessions: number;
  totalUsers: number;
  activeUsers: number;
  pageviews: number;
  deltas: {
    sessions: number | null;
    totalUsers: number | null;
    activeUsers: number | null;
    pageviews: number | null;
  };
}

export interface ReportSourceMixTrendRow {
  date: string;
  channel: string;
  sessions: number;
  totalUsers: number;
  activeUsers: number;
  pageviews: number;
}

export interface ReportSourceMix {
  perChannel: ReportSourceMixChannelRow[];
  trend: ReportSourceMixTrendRow[];
  trendCompare?: ReportSourceMixTrendRow[];
  socialShare: { sessions: number; delta: number | null };
}

// M13.4 — ecommerce types.
export interface ReportEcommerceDeltas {
  purchases: number | null;
  revenue: number | null;
  averageOrderValue: number | null;
  revenuePerUser: number | null;
  purchaserConversionRate: number | null;
}

export interface ReportEcommerceChannelRow {
  channel: string;
  purchases: number;
  revenue: number;
  averageOrderValue: number;
  revenuePerUser: number;
  purchaserConversionRate: number;
  deltas: ReportEcommerceDeltas;
}

export interface ReportEcommercePlatformRow {
  platform: CanonicalPlatform;
  purchases: number;
  revenue: number;
  averageOrderValue: number;
  revenuePerUser: number;
  purchaserConversionRate: number;
  deltas: ReportEcommerceDeltas;
}

export interface ReportEcommerceTrendRow {
  date: string;
  channel: string;
  revenue: number;
  purchases: number;
}

export interface ReportEcommerce {
  detected: true;
  windowActive: boolean;
  perChannel: ReportEcommerceChannelRow[];
  perPlatform: ReportEcommercePlatformRow[];
  trend: ReportEcommerceTrendRow[];
  trendCompare?: ReportEcommerceTrendRow[];
}

// M12.2 — fetchReport accepts either a preset range (legacy) OR explicit
// start/end bounds. Bounds take precedence when both are provided. The
// server accepts the same dual shape so the client can migrate at its
// own pace.
export type FetchReportArgs =
  | { propertyId: string; range?: ReportRange; compare?: ReportCompare }
  | {
      propertyId: string;
      startDate: string;
      endDate: string;
      compare?: ReportCompare;
      /** Required when compare === "custom"; ignored otherwise. */
      compareStart?: string;
      compareEnd?: string;
    };

function hasBounds(args: FetchReportArgs): args is {
  propertyId: string;
  startDate: string;
  endDate: string;
  compare?: ReportCompare;
  compareStart?: string;
  compareEnd?: string;
} {
  return (
    typeof (args as { startDate?: string }).startDate === "string" &&
    typeof (args as { endDate?: string }).endDate === "string"
  );
}

export async function fetchReport(args: FetchReportArgs): Promise<ReportPayload> {
  const compare = args.compare ?? "previous";
  const params = new URLSearchParams({ propertyId: args.propertyId, compare });
  if (hasBounds(args)) {
    params.set("start", args.startDate);
    params.set("end", args.endDate);
    if (compare === "custom" && args.compareStart && args.compareEnd) {
      params.set("compareStart", args.compareStart);
      params.set("compareEnd", args.compareEnd);
    }
  } else {
    params.set("range", args.range ?? "last_28d");
  }
  const res = await fetch(`/api/report?${params.toString()}`, { credentials: "same-origin" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`report failed: ${res.status}${text ? ` — ${text}` : ""}`);
  }
  return (await res.json()) as ReportPayload;
}

/**
 * Infer which preset (if any) matches a (startDate, endDate) window
 * against browser-local "yesterday". Returns the preset enum on a
 * match, else "custom". Used by the DateRangeSelector to highlight the
 * correct pill, and by URL-reading pages to decide which subtitle
 * helper to call. Not exported from the server; duplicated here to
 * avoid pulling date-fns into the client bundle for one function.
 */
export function inferPresetLabel(
  startDate: string,
  endDate: string,
  today: Date = new Date(),
): ReportMetaRange {
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const end = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;
  if (endDate !== end) return "custom";
  const msPerDay = 24 * 60 * 60 * 1000;
  const startMs = new Date(`${startDate}T00:00:00Z`).getTime();
  const endMs = new Date(`${endDate}T00:00:00Z`).getTime();
  const days = Math.round((endMs - startMs) / msPerDay) + 1;
  if (days === 7) return "last_7d";
  if (days === 28) return "last_28d";
  if (days === 90) return "last_90d";
  return "custom";
}

// M12.2 — createShareLink accepts bounds (startDate + endDate) and/or
// a preset range. When both are set, the server prefers bounds and
// treats the preset as the fallback label on the share envelope.
//
// M12.12 — `compare === "custom"` requires `compareStart` + `compareEnd`
// at the server. Callers must pass both when compare is custom or the
// POST 400s.
export interface CreateShareLinkArgs {
  propertyId: string;
  range?: ReportRange;
  startDate?: string;
  endDate?: string;
  compare?: ReportCompare;
  compareStart?: string;
  compareEnd?: string;
  expiresInDays: number;
}

export interface ShareLinkResponse {
  token: string;
  url: string;
  expiresAt: number;
  expiresInDays: number;
}

export async function createShareLink(args: CreateShareLinkArgs): Promise<ShareLinkResponse> {
  const res = await fetch("/api/share", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(body.message ?? body.error ?? `share failed: ${res.status}`);
  }
  return (await res.json()) as ShareLinkResponse;
}

// Legacy paragraph/bullets shape — kept for the share envelope (v3),
// the digest, and the PDF export (which all freeze it at mint time).
// The dashboard reads `insightsV2` and ignores this field.
export interface InsightsResult {
  paragraph: string;
  bullets: string[];
}

export interface InsightsResponse {
  ok: true;
  insights: InsightsResult;
  insightsV2: InsightsV2;
  telemetry: InsightsTelemetry;
  cached: boolean;
}

export interface InsightsErrorResponse {
  ok: false;
  error: string;
  /** Structured reason code from the hardening layer. Populated for
   *  every /api/insights failure; may be absent on older server
   *  responses. See `/api/insights` in `src/server/routes/api.ts` for
   *  the full taxonomy. */
  reason?:
    | "openrouter_transport"
    | "openrouter_http"
    | "parse_failure"
    | "timeout_budget"
    | "internal";
  /** openrouter_transport only. */
  transportError?: "timeout" | "abort" | "network";
  /** openrouter_http only — raw HTTP status from OpenRouter. */
  status?: number;
  /** openrouter_http only — parsed `error.code` from the provider body. */
  providerError?: string | null;
  /** openrouter_http only — parsed `error.message` from the provider body. */
  providerMessage?: string | null;
  /** parse_failure only — first ~280 chars of the raw LLM output, so
   *  the error panel can surface what actually came back. */
  rawContentPreview?: string;
  /** timeout_budget only — wall-clock elapsed (ms) when the total
   *  budget tripped. */
  elapsedMs?: number;
  /** timeout_budget only — configured budget (ms). */
  budgetMs?: number;
  /** SKE-3452 — every model the chain orchestrator attempted, in order.
   *  Populated on every /api/insights error. Helpful for triage: a
   *  single-entry chain means the primary failed fast without the
   *  orchestrator getting a shot at fallback. */
  fallbackChain?: string[];
  /** SKE-3452 — was this request running against ESCALATION_CHAIN
   *  (the "try harder" path) rather than the default FALLBACK_CHAIN.
   *  Surfaced so the dev picker chip can disambiguate. */
  escalated?: boolean;
  /** Human-readable message. Safe to display directly in the UI. */
  message: string;
}

export interface FetchInsightsArgs {
  propertyId: string;
  startDate: string;
  endDate: string;
  compare: ReportCompare;
  compareStart?: string;
  compareEnd?: string;
  /** Bypasses the 24h cache + repopulates. Subject to the
   *  INSIGHTS_REGEN bucket (20/hour/(user, property)). */
  regenerate?: boolean;
  /** SKE-3456 — "try harder" retry flag. When true, the server runs
   *  the request against ESCALATION_CHAIN (gpt-5.4-mini → gpt-5.4 →
   *  haiku-4.5) instead of the cheap-tier FALLBACK_CHAIN. Only set
   *  from the error-panel "Try again" button, never from the happy-
   *  path Regenerate. Subject to the tighter `INSIGHTS_ESCALATE`
   *  bucket (5/hour/(user, property)). */
  escalate?: boolean;
}

/**
 * Distinguished error thrown when /api/insights returns 429 — surfaced
 * by InsightsSection so the UI can swap to a cooldown message rather
 * than the generic placeholder.
 */
export class InsightsRateLimitedError extends Error {
  constructor(public retryAfterSeconds: number) {
    super(`Rate limited. Try again in ${retryAfterSeconds}s.`);
    this.name = "InsightsRateLimitedError";
  }
}

/**
 * Distinguished error for 5xx / openrouter failure paths. Carries the
 * structured reason + any provider detail from the server so the UI
 * can render a targeted error panel (transport timeout vs. HTTP 4xx
 * vs. empty generation) with a human-readable message.
 */
export class InsightsUnavailableError extends Error {
  public readonly reason: NonNullable<InsightsErrorResponse["reason"]> | "unknown";
  public readonly status: number;
  public readonly transportError: InsightsErrorResponse["transportError"];
  public readonly providerStatus: InsightsErrorResponse["status"];
  public readonly providerError: InsightsErrorResponse["providerError"];
  public readonly providerMessage: InsightsErrorResponse["providerMessage"];
  public readonly rawContentPreview: InsightsErrorResponse["rawContentPreview"];
  public readonly elapsedMs: InsightsErrorResponse["elapsedMs"];
  public readonly budgetMs: InsightsErrorResponse["budgetMs"];
  public readonly fallbackChain: InsightsErrorResponse["fallbackChain"];
  public readonly escalated: InsightsErrorResponse["escalated"];

  constructor(httpStatus: number, body?: InsightsErrorResponse) {
    super(body?.message ?? "AI summary temporarily unavailable.");
    this.name = "InsightsUnavailableError";
    this.reason = body?.reason ?? "unknown";
    this.status = httpStatus;
    this.transportError = body?.transportError;
    this.providerStatus = body?.status;
    this.providerError = body?.providerError;
    this.providerMessage = body?.providerMessage;
    this.rawContentPreview = body?.rawContentPreview;
    this.elapsedMs = body?.elapsedMs;
    this.budgetMs = body?.budgetMs;
    this.fallbackChain = body?.fallbackChain;
    this.escalated = body?.escalated;
  }
}

/**
 * SKE-3471 — Client-side abort ceiling. Set past the server-side 60s
 * total budget (and 45s per-call timeout inside it) so the server
 * almost always returns first with a structured error. This only
 * fires if the server, Cloudflare edge, or network hang outright.
 * When it does fire, the AbortError propagates up as a normal Error
 * instance — the InsightsSection catch block's fallthrough converts
 * it to `status: "error"` and un-sticks the Regenerate button.
 *
 * Chosen at 65s: 60s server budget + ~5s for JSON body read + HTTP
 * round-trip + error-response serialization. Anything longer than
 * that is a pathological path (502 retry loop at the edge, etc.) that
 * we want the UI to bail from fast, not keep spinning on.
 */
const INSIGHTS_CLIENT_TIMEOUT_MS = 65_000;

export async function fetchInsights(args: FetchInsightsArgs): Promise<InsightsResponse> {
  const params = new URLSearchParams({
    propertyId: args.propertyId,
    start: args.startDate,
    end: args.endDate,
    compare: args.compare,
  });
  if (args.compare === "custom" && args.compareStart && args.compareEnd) {
    params.set("compareStart", args.compareStart);
    params.set("compareEnd", args.compareEnd);
  }
  if (args.regenerate) params.set("regenerate", "1");
  if (args.escalate) params.set("escalate", "1");

  const controller = new AbortController();
  const timer =
    typeof window !== "undefined"
      ? window.setTimeout(() => controller.abort(), INSIGHTS_CLIENT_TIMEOUT_MS)
      : null;

  let res: Response;
  try {
    res = await fetch(`/api/insights?${params.toString()}`, {
      credentials: "same-origin",
      signal: controller.signal,
    });
  } finally {
    if (timer !== null) window.clearTimeout(timer);
  }

  if (res.status === 429) {
    const retryAfter = Number.parseInt(res.headers.get("Retry-After") ?? "60", 10);
    throw new InsightsRateLimitedError(Number.isFinite(retryAfter) ? retryAfter : 60);
  }
  // Non-2xx: parse the structured body so the UI can render provider
  // detail. If the body isn't JSON or the server is older, fall back
  // to an unknown-reason error with a generic message. This includes
  // 502 / 503 / 504 responses, which must always throw so the
  // InsightsSection catch block flips status out of "loading" rather
  // than leaving the Regenerate button stuck spinning.
  if (!res.ok) {
    let body: InsightsErrorResponse | undefined;
    try {
      body = (await res.json()) as InsightsErrorResponse;
    } catch {
      body = undefined;
    }
    throw new InsightsUnavailableError(res.status, body);
  }
  const body = (await res.json()) as InsightsResponse | InsightsErrorResponse;
  if (!body.ok) throw new InsightsUnavailableError(res.status, body);
  return body;
}

export interface SharedReportProperty {
  propertyId: string;
  propertyName: string;
}

export interface SharedReportEnvelope {
  property: SharedReportProperty;
  createdAt: number;
  report: ReportPayload;
  // M12.3 — v2 envelope freezes resolved bounds + compare bounds at mint
  // time. Undefined on legacy v1 snapshots; callers should fall back to
  // payload.meta fields when absent (those have been populated since M9).
  startDate?: string;
  endDate?: string;
  compareStart?: string | null;
  compareEnd?: string | null;
  rangeLabel?: ReportMetaRange;
  // M13.6 — v3 envelope freezes the AI insights string at mint time.
  // Null when generation failed during mint; absent on legacy v1/v2
  // envelopes (the share viewer simply omits the section in both cases).
  insights?: InsightsResult | null;
  // SKE-3458 — v4 envelope freezes the *structured* V2 insights shape
  // alongside legacy so the shared viewer can render the same headline
  // + preview + "See more detail" disclosure + action cards the owner
  // saw on their dashboard. Absent on v1/v2/v3 shares (viewer falls
  // back to `insights` legacy rendering for v3, omits entirely for
  // v1/v2).
  insightsV2?: InsightsV2 | null;
}

export type FetchSharedReportResult =
  | ({ kind: "ok" } & SharedReportEnvelope)
  | { kind: "expired"; expiresAt: number }
  | { kind: "not_found" };

export async function fetchSharedReport(token: string): Promise<FetchSharedReportResult> {
  const res = await fetch(`/api/share/${encodeURIComponent(token)}`, { credentials: "omit" });
  if (res.status === 404) return { kind: "not_found" };
  if (res.status === 410) {
    const body = (await res.json().catch(() => ({}))) as { expiresAt?: number };
    return { kind: "expired", expiresAt: body.expiresAt ?? Date.now() };
  }
  if (!res.ok) throw new Error(`share fetch failed: ${res.status}`);
  const body = (await res.json()) as unknown;

  // Envelope shipped by M9.x — property + createdAt + payload. Older
  // snapshots (pre-envelope) shipped just the ReportPayload at the top
  // level; detect and fall back so pre-existing share URLs still work.
  if (body && typeof body === "object" && "payload" in body && "property" in body) {
    const env = body as {
      schemaVersion?: number;
      property: SharedReportProperty;
      createdAt?: number;
      payload: ReportPayload;
      startDate?: string;
      endDate?: string;
      compareStart?: string | null;
      compareEnd?: string | null;
      rangeLabel?: ReportMetaRange;
      insights?: InsightsResult | null;
      insightsV2?: InsightsV2 | null;
    };
    // SKE-3458 — v4 envelopes carry everything v3 carried plus the
    // frozen V2 structured insights (headline + previewBullets +
    // expandedParagraphs + actions). Shared viewer renders these with
    // a show/collapse disclosure + action cards, matching the
    // dashboard layout minus the regenerate / model-picker controls.
    if (env.schemaVersion === 4) {
      return {
        kind: "ok",
        property: env.property,
        createdAt: env.createdAt ?? Date.now(),
        report: env.payload,
        startDate: env.startDate,
        endDate: env.endDate,
        compareStart: env.compareStart ?? null,
        compareEnd: env.compareEnd ?? null,
        rangeLabel: env.rangeLabel,
        insights: env.insights ?? null,
        insightsV2: env.insightsV2 ?? null,
      };
    }
    // M13.6 — v3 envelopes carry everything v2 carried plus a frozen
    // AI insights block. The shared viewer reads this verbatim and
    // renders FrozenInsightsSection without a regenerate button.
    if (env.schemaVersion === 3) {
      return {
        kind: "ok",
        property: env.property,
        createdAt: env.createdAt ?? Date.now(),
        report: env.payload,
        startDate: env.startDate,
        endDate: env.endDate,
        compareStart: env.compareStart ?? null,
        compareEnd: env.compareEnd ?? null,
        rangeLabel: env.rangeLabel,
        insights: env.insights ?? null,
      };
    }
    // M12.3 — v2 envelopes ship the frozen bounds + compare bounds +
    // rangeLabel alongside the payload. Surface them to callers so the
    // viewer can render them verbatim without leaning on payload.meta.
    if (env.schemaVersion === 2) {
      return {
        kind: "ok",
        property: env.property,
        createdAt: env.createdAt ?? Date.now(),
        report: env.payload,
        startDate: env.startDate,
        endDate: env.endDate,
        compareStart: env.compareStart ?? null,
        compareEnd: env.compareEnd ?? null,
        rangeLabel: env.rangeLabel,
      };
    }
    return {
      kind: "ok",
      property: env.property,
      createdAt: env.createdAt ?? Date.now(),
      report: env.payload,
    };
  }
  return {
    kind: "ok",
    property: { propertyId: "unknown", propertyName: "Shared report" },
    createdAt: Date.now(),
    report: body as ReportPayload,
  };
}

export interface MineShareLink {
  id: string;
  propertyId: string;
  token: string;
  url: string;
  expiresAt: number;
  createdAt: number;
}

export async function listMyShareLinks(): Promise<MineShareLink[]> {
  const res = await fetch("/api/shares", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`list share links failed: ${res.status}`);
  const body = (await res.json()) as { shares: MineShareLink[] };
  return body.shares;
}

export async function revokeMyShareLink(id: string): Promise<void> {
  const res = await fetch(`/api/shares/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`revoke share failed: ${res.status}`);
}

export async function revokeExportDownload(key: string): Promise<void> {
  const res = await fetch(`/api/export/download/${encodeURIComponent(key)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`revoke export download failed: ${res.status}`);
}

// ---------------------------------------------------------------------------
// M12.4 — per-property conversion event configuration.
// ---------------------------------------------------------------------------

/** Warnings returned by the PUT conversion-events endpoint when a
 *  user-entered event name didn't fire in the last 365 days. The event
 *  still persists; the caller surfaces the warning next to the row. */
export interface ConversionEventWarning {
  eventName: string;
  reason: string;
}

export interface SaveConversionEventsResult {
  ok: boolean;
  events: string[];
  warnings: ConversionEventWarning[];
}

export async function fetchPropertyEvents(propertyId: string): Promise<string[]> {
  const res = await fetch(`/api/properties/${encodeURIComponent(propertyId)}/events`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetch property events failed: ${res.status}`);
  const body = (await res.json()) as { events: string[] };
  return body.events;
}

export async function fetchConversionEvents(propertyId: string): Promise<string[]> {
  const res = await fetch(`/api/properties/${encodeURIComponent(propertyId)}/conversion-events`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetch conversion events failed: ${res.status}`);
  const body = (await res.json()) as { events: string[] };
  return body.events;
}

export async function saveConversionEvents(
  propertyId: string,
  events: string[],
): Promise<SaveConversionEventsResult> {
  const res = await fetch(`/api/properties/${encodeURIComponent(propertyId)}/conversion-events`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ events }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(body.message ?? body.error ?? `save conversion events failed: ${res.status}`);
  }
  const body = (await res.json()) as {
    ok: boolean;
    events?: string[];
    warnings?: ConversionEventWarning[];
  };
  return {
    ok: body.ok,
    events: body.events ?? events,
    warnings: body.warnings ?? [],
  };
}
